import {CircularProgress, InputAdornment} from '@material-ui/core';
import {Async, isLoaded, isLoading} from 'Core/models/Async';
import {matchPhoneNumber, PhoneNumber, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {Either, isLeft, isRight} from 'fp-ts/lib/Either';
import {constant} from 'fp-ts/lib/function';
import React, {useEffect, useState} from 'react';
import {Errors} from 'Shared/components/ErrorPopover';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import './ValidatableTextInput.sass';

export type ValidatablePhoneInputProps = {
  value: Async<Either<PhoneNumber, ValidPhoneNumber>>;
  onValidate: (phone: string) => void;
  label: string;
  className?: string;
  disabled?: boolean;
};

const toMasked = (phoneNumber: string) => {
  if (phoneNumber.length > 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  } else if (phoneNumber.length > 3) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}`;
  }
  if (phoneNumber.length > 3) {
    return `(${phoneNumber})`;
  } else {
    return phoneNumber;
  }
};

const clearMask = (phoneNumber: string) => {
  const phoneNumbers = phoneNumber.match(/\d{1,9}/g);

  return phoneNumbers ? phoneNumbers.join('') : '';
};

export const getEitherValue = <TLeft, TRight>(value: Either<TLeft, TRight>) =>
  isLeft(value) ? value.left : value.right;

const ValidatablePhoneInput = (props: ValidatablePhoneInputProps) => {
  const [hasBlurredPhone, setHasBlurredPhone] = useState(false);
  const getIncompletePhoneError = (phone: PhoneNumber) =>
    matchPhoneNumber(phone, {
      InvalidPhoneNumber: invalid => [invalid.validationError],
      UnvalidatedPhoneNumber: constant([]),
      ValidPhoneNumber: constant([]),
    });
  const errors = isLoaded(props.value)
    ? Errors.standardErrors({
        value:
          isLeft(props.value.Data) && !(props.value.Data.left instanceof ValidPhoneNumber)
            ? getIncompletePhoneError(props.value.Data.left)
            : isRight(props.value.Data)
            ? []
            : ['Phone Number is already taken.'],
      })
    : Errors.standardErrors({value: []});

  useEffect(() => {
    if (isLoaded(props.value) && getEitherValue(props.value.Data) instanceof ValidPhoneNumber) {
      setHasBlurredPhone(true);
    }
  }, [props.value]);

  return isLoaded(props.value) || isLoading(props.value) ? (
    <ValidatableTextField
      InputProps={{
        endAdornment: <InputAdornment position="end">{isLoading(props.value) && <CircularProgress />}</InputAdornment>,
      }}
      className={`${isLoaded(props.value) && isRight(props.value.Data) && 'validatable-text-input'} ${props.className}`}
      placeholder="(XXX) XXX-XXXX"
      disabled={props.disabled}
      value={toMasked(getEitherValue(props.value.Data!).phoneNumber)}
      showErrors={errors => hasBlurredPhone && errors.value.length > 0}
      inputProps={{
        maxLength: '14',
      }}
      errors={errors}
      onChange={phoneNumber => {
        props.onValidate(clearMask(phoneNumber) || '');
      }}
      onBlur={() => setHasBlurredPhone(true)}
      label={props.label || 'Phone Number'}
    />
  ) : (
    <>An error has occurred.</>
  );
};

export default ValidatablePhoneInput;
